import type { RouteObject } from 'react-router';
import { Gate } from './features/auth/Gate';
import { NoAccess } from './features/auth/NoAccess';
import { Home } from './features/shell/Home';
import { Shell } from './features/shell/Shell';
import { Terminals } from './features/terminals/Terminals';
import { DoorEvents } from './features/terminals/DoorEvents';
import { Audit } from './features/audit/Audit';
import { Bookings } from './features/bookings/Bookings';
import { ContractNew } from './features/contracts/ContractNew';
import { Contracts } from './features/contracts/Contracts';
import { Enquiries } from './features/enquiries/Enquiries';
import { Invoices } from './features/invoices/Invoices';
import { Members } from './features/members/Members';
import { Tasks } from './features/tasks/Tasks';
import { CreatePlan } from './features/plans/CreatePlan';
import { Plans } from './features/plans/Plans';
import { Settings } from './features/settings/Settings';
import { Publish } from './features/plans/Publish';
import { Stickers } from './features/plans/Stickers';
import { Editor } from './features/plans/Editor';
import { Upload } from './features/plans/Upload';
import { Login } from './features/auth/Login';
import { LoginCode } from './features/auth/LoginCode';
import { LoginTotp } from './features/auth/LoginTotp';
import { LoginChallenge } from './features/auth/LoginChallenge';
import { AuthCallback } from './features/auth/AuthCallback';
import { RouteError } from './features/shell/RouteError';

/**
 * Backoffice routes (10-web-admin § 3).
 *
 * Everything below `Gate` requires a role. `/kein-zugang` sits outside it,
 * because that is where the gate sends an account without one.
 */
const pages: RouteObject[] = [
  { path: '/login', element: <Login /> },
  { path: '/login/code', element: <LoginCode /> },
  { path: '/login/totp', element: <LoginTotp /> },
  { path: '/login/challenge', element: <LoginChallenge /> },
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/kein-zugang', element: <NoAccess /> },
  {
    element: <Gate />,
    children: [
      {
        element: <Shell />,
        children: [
          { path: '/', element: <Home /> },
          { path: '/buchungen', element: <Bookings /> },
          // § 9.3 — the manual-booking dialog is a route, like the panels.
          { path: '/buchungen/neu', element: <Bookings /> },
          { path: '/buchungen/:bookingId', element: <Bookings /> },
          // § 3 — the open detail panel is a child route, so it is in the URL
          // and survives a reload and a shared link.
          { path: '/mitglieder', element: <Members /> },
          { path: '/mitglieder/:userId', element: <Members /> },
          { path: '/vertraege', element: <Contracts /> },
          { path: '/vertraege/neu', element: <ContractNew /> },
          { path: '/vertraege/:contractId', element: <Contracts /> },
          { path: '/rechnungen', element: <Invoices /> },
          { path: '/rechnungen/:invoiceId', element: <Invoices /> },
          { path: '/anfragen', element: <Enquiries /> },
          { path: '/anfragen/:id', element: <Enquiries /> },
          { path: '/aufgaben', element: <Tasks /> },
          { path: '/grundrisse', element: <Plans /> },
          { path: '/grundrisse/neu', element: <CreatePlan /> },
          { path: '/grundrisse/:planId/upload', element: <Upload /> },
          { path: '/grundrisse/:planId/editor', element: <Editor /> },
          { path: '/grundrisse/:planId/aufkleber', element: <Stickers /> },
          { path: '/grundrisse/:planId/veroeffentlichen', element: <Publish /> },
          { path: '/terminals', element: <Terminals /> },
          { path: '/terminals/ereignisse', element: <DoorEvents /> },
          { path: '/einstellungen', element: <Settings /> },
          { path: '/einstellungen/:section', element: <Settings /> },
          { path: '/audit', element: <Audit /> },
        ],
      },
    ],
  },
];

/** 04 § 15 — one root error boundary over every page (03 § 4.14 `genericError`). */
export const routes: RouteObject[] = [{ errorElement: <RouteError />, children: pages }];
